import { Box, Checkbox, Text, useBreakpointValue } from "@chakra-ui/react";
import { ReactElement, useCallback, useMemo, useState } from "react";
import {
  GroupBase,
  OptionProps,
  SingleValue,
  components,
} from "chakra-react-select";

import { Select } from "components/Select";
import { useAuth } from "contexts/AuthProvider";
import useCustomers from "hooks/api/customers/useCustomers";
import useQueryParam, { QUERY_STRING_KEYS } from "hooks/useQueryParam";
import { AI_EVENTS, useTrackEvent } from "hooks/useAppInsight";
import useSelectedCustomerCode from "hooks/useSelectedCustomerCode";
import textStyles from "theme/textStyles";
import { inputSize, mobileStyleBreakpoint } from "util/styleHelpers";

const SELECT_MAX_WIDTH = mobileStyleBreakpoint("unset", "480px");
const customerToSelectOption = (customer: Customer): SelectOption => ({
  value: customer.code,
  label: `${customer.code} - ${customer.name}, ${customer.currency}`,
});

const Option = (props: OptionProps<unknown, boolean, GroupBase<unknown>>) => (
  <components.Option {...props}>
    <Checkbox
      pointerEvents="none"
      top={1}
      isChecked={props.isSelected}
      readOnly
    />
    <Box as="label" paddingLeft={2}>
      {props.label}
    </Box>
  </components.Option>
);

const CustomerSelect = (): ReactElement => {
  const [searchValue, setSearchValue] = useState("");

  const { username } = useAuth();

  const size = useBreakpointValue(inputSize, { ssr: false });

  const { data: customers, isLoading } = useCustomers();

  const [, setSelectedCustomerCodes] = useQueryParam(
    QUERY_STRING_KEYS.SELECTED_CUSTOMER_KEY
  );
  const selectedCustomerCodes = useSelectedCustomerCode();

  const onChangeCustomerCode = useTrackEvent(
    AI_EVENTS.CHANGE_CUSTOMER_CARD_CARD,
    {
      cardCodes: selectedCustomerCodes,
      username,
    }
  );

  const onSelectCustomers = useCallback(
    (options: SingleValue<SelectOption>[]) => {
      setSelectedCustomerCodes(
        options?.map((option) => option?.value).join(",")
      );
      onChangeCustomerCode();
    },
    [onChangeCustomerCode, setSelectedCustomerCodes]
  );

  const options = useMemo(
    () => customers?.map(customerToSelectOption),
    [customers]
  );

  const selectedCustomerOptions = useMemo(() => {
    return selectedCustomerCodes.map((code) => {
      const selectedCustomer = customers?.find(
        (customer) => customer.code === code
      );
      return selectedCustomer
        ? customerToSelectOption(selectedCustomer)
        : undefined;
    });
  }, [customers, selectedCustomerCodes]);

  const optionsWithDisabled = options?.map((option) => ({
    ...option,
    isDisabled:
      selectedCustomerOptions.length >= 10 &&
      !selectedCustomerOptions.some(
        (selectedOption) => selectedOption?.value === option.value
      ),
  }));

  const groupedOptions =
    options && options?.length > 10
      ? [
          {
            label: "Select up to 10 customer codes",
            options: optionsWithDisabled,
          },
        ]
      : optionsWithDisabled;

  const formatGroupLabel = (data: GroupBase<unknown>) => (
    <Box>
      <Text fontSize="md" fontStyle="italic" fontWeight="bold">
        {data.label}
      </Text>
    </Box>
  );

  return (
    <Select
      backspaceRemovesValue={false}
      controlShouldRenderValue={false}
      closeMenuOnSelect={false}
      blurInputOnSelect={false}
      components={{
        Option,
      }}
      size={size as any}
      value={selectedCustomerOptions}
      onChange={onSelectCustomers as any}
      isClearable={false}
      isSearchable={true}
      onInputChange={(value, action) => {
        if (action.action === "input-change") {
          setSearchValue(value);
        }
      }}
      inputValue={searchValue}
      onMenuClose={() => setSearchValue("")}
      options={groupedOptions}
      formatGroupLabel={formatGroupLabel}
      isDisabled={(customers?.length ?? 0) <= 1}
      isLoading={isLoading}
      isMulti={true}
      placeholder={
        <Box
          color="grey.600"
          fontSize="md"
          fontWeight="semibold"
          paddingLeft={2}
          width="368px"
        >
          Viewing {selectedCustomerOptions.length} of {options?.length} Customer
          Codes
        </Box>
      }
      styles={{
        option: (provided, state) => ({
          ...provided,
          borderBottom: "1px solid #E0E1E3",
          padding: "8px",
          color: state.isDisabled ? "#A3A4A6" : "unset",
          backgroundColor: "unset",
          ":hover": {
            backgroundColor: "#DAF8FF",
          },
        }),
      }}
      selectChakraStyleOverrides={{
        input: (provided) => ({
          ...provided,
          width: "400px",
          paddingLeft: 2,
        }),
        control: (provided) => ({
          ...provided,
          maxH: "84px",
          overflowY: "auto",
          paddingLeft: 3,
        }),
        container: (provided) => ({
          ...provided,
          backgroundColor: "white",
          maxW: SELECT_MAX_WIDTH,
        }),
        multiValue: (provided) => ({
          ...provided,
          ...textStyles.label2,
          backgroundColor: "white",
        }),
        menuList: (provided) => ({
          ...provided,
          bgColor: "grey.50",
          maxHeight: "400px",
        }),
        groupHeading(provided) {
          return {
            ...provided,
            backgroundColor: "grey.50",
            padding: 2,
            position: "sticky",
            borderBottom: "1px solid #E0E1E3",
          };
        },
      }}
    />
  );
};

export default CustomerSelect;
